/**
 * First-play guidance on the sector screen (the coordinator's row, Oct 10): plain one-line hints at the point of the
 * action, for the first days of play — scout before you attack, refit at a rest point, what the worth and forecast
 * lines mean, how a turn resolves. Each is dismissable; all of them go quiet once a first scenario is finished (or
 * the player turns them off). Plain text, with the fiction layer's wording in `flavor` (rendered as `data-flavor`).
 * Pure: which hint fits a moment, and the saved state's shape; `sectorScreen.ts` draws them and keeps the state.
 */

export type HintId = 'orders' | 'scout' | 'refit' | 'worth' | 'forecast' | 'battle' | 'resolve' | 'brief';

export interface Hint { id: HintId; text: string; flavor: string }

export const HINTS: Record<HintId, Hint> = {
  brief: { id: 'brief', text: 'Read the brief, then click one of your squads to give it an order.', flavor: 'Read the contract brief, then click one of your crews to assign it.' },
  orders: { id: 'orders', text: 'Pick an order mode (move, sneak, scout…), then click a system on the map.', flavor: 'Pick a tasking (move, sneak, scout…), then click a system on the map.' },
  scout: { id: 'scout', text: 'Scout before you attack: what you know of a place is only as fresh as your last look.', flavor: 'Survey before you commit: an old report is a guess with a date on it.' },
  refit: { id: 'refit', text: 'This squad is at a rest point (home or a depot): refit its kit or buy drones here before it leaves.', flavor: 'This crew is at a depot: requisition kit or drones before it ships out.' },
  worth: { id: 'worth', text: 'Worth is what a system is good for: intact planets, what is built there, a home. The enemy marches on it too.', flavor: 'Worth is what a holding pays: intact worlds, what is built, a home. The other contractor wants it too.' },
  forecast: { id: 'forecast', text: 'The forecast shows the next days’ weather here; a star marks fog, storm or dust, which hide a sneaking approach.', flavor: 'The forecast shows the next days here; a star marks weather that hides an approach.' },
  battle: { id: 'battle', text: 'A battle is due: auto-resolve it, watch it, or take command and play it yourself.', flavor: 'An engagement is due: let it resolve, watch it, or take the field yourself.' },
  resolve: { id: 'resolve', text: 'When your squads have their orders, press Resolve turn: a day passes and everyone moves at once.', flavor: 'When your crews are tasked, resolve the turn: a day passes and everyone moves at once.' },
};

/** What the screen is showing, for picking the hints that fit. */
export interface HintContext {
  day: number;
  scenario: boolean;
  squadSelected: boolean;
  /** The selected squad stands at a rest point. */
  atRestPoint: boolean;
  /** The selected system's report is old or missing, and a squad of ours is selected. */
  staleTarget: boolean;
  systemSelected: boolean;
  worthShown: boolean;
  forecastShown: boolean;
  battlePending: boolean;
  anyOrdersGiven: boolean;
}

/** The saved state: hints dismissed one by one, and all off (by the player, or a finished first scenario). */
export interface HintsState { dismissed: HintId[]; off: boolean }

export const HINTS_STORAGE_KEY = 'orbituary-sector-hints';
export const HINT_DAYS = 6;

export function normalizeHints(raw: unknown): HintsState {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const ids = Object.keys(HINTS) as HintId[];
  return {
    dismissed: Array.isArray(r.dismissed) ? (r.dismissed as unknown[]).filter((x): x is HintId => ids.includes(x as HintId)) : [],
    off: r.off === true,
  };
}

/**
 * The hints that fit now, in the place they belong: `panel` (the side panel's top), `system` (the system card), `squad`
 * (the squad card), `battle` (the battle card). Nothing after the first days or once off; a dismissed hint never again.
 */
export function hintsFor(ctx: HintContext, state: HintsState): Partial<Record<'panel' | 'system' | 'squad' | 'battle', Hint>> {
  if (state.off || ctx.day >= HINT_DAYS) return {};
  const ok = (id: HintId) => !state.dismissed.includes(id);
  const out: Partial<Record<'panel' | 'system' | 'squad' | 'battle', Hint>> = {};
  if (ctx.battlePending && ok('battle')) out.battle = HINTS.battle;
  if (!ctx.squadSelected && !ctx.systemSelected) {
    if (ctx.scenario && ctx.day < 1 && ok('brief')) out.panel = HINTS.brief;
    else if (ctx.anyOrdersGiven && ok('resolve')) out.panel = HINTS.resolve;
  }
  if (ctx.squadSelected) {
    if (ctx.staleTarget && ok('scout')) out.squad = HINTS.scout;
    else if (ctx.atRestPoint && ok('refit')) out.squad = HINTS.refit;
    else if (ok('orders')) out.squad = HINTS.orders;
  }
  if (ctx.systemSelected) {
    if (ctx.staleTarget && ok('scout')) out.system = HINTS.scout;
    else if (ctx.worthShown && ok('worth')) out.system = HINTS.worth;
    else if (ctx.forecastShown && ok('forecast')) out.system = HINTS.forecast;
  }
  return out;
}
